import { useState, useEffect } from "react";
import { gitlabApiPrefix } from "../components/SupabaseClient";
import { fetchAllGitLabPages } from "../utils/gitlabPagination";

/**
 * Fetches container registry repositories and tag details for a single GitLab project.
 * Only runs when `projectId` is non-null (triggered by the user selecting a project).
 *
 * Fetch sequence per project:
 *   A) GET /projects/:id/registry/repositories          – list repositories
 *   B) GET /.../:repo_id/tags                           – list tags per repository
 *   C) GET /.../:repo_id/tags/:tag_name                 – detail (digest, total_size, created_at)
 *
 * Steps B and C run concurrently for multiple repositories using Promise.all.
 *
 * @param {number|string|null} projectId  GitLab project ID
 * @returns {{
 *   repositories: Array<{
 *     id: number,
 *     name: string,
 *     path: string,
 *     location: string,
 *     tags: Array<{ name: string, digest: string, total_size: number, created_at: string }>
 *   }>,
 *   loading: boolean,
 *   error: string|null,
 * }}
 */
export const useGitLabRegistryTags = (projectId) => {
  const [repositories, setRepositories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!projectId) {
      setRepositories([]);
      setError(null);
      return;
    }

    // Guard prevents a stale/slow response from overwriting newer state or
    // calling setState after unmount.
    const controller = new AbortController();
    const { signal } = controller;

    const fetchRegistry = async () => {
      setLoading(true);
      setError(null);

      try {
        // Step A: list registry repositories for this project.
        const repos = await fetchAllGitLabPages(
          `${gitlabApiPrefix}/api/v4/projects/${projectId}/registry/repositories?per_page=100`,
          { signal },
        );

        // Steps B + C: for each repository, fetch tags then fetch each tag's details.
        const reposWithTags = await Promise.all(
          repos.map(async (repo) => {
            // Step B: list tags.
            let tags;
            try {
              tags = await fetchAllGitLabPages(
                `${gitlabApiPrefix}/api/v4/projects/${projectId}/registry/repositories/${repo.id}/tags?per_page=100`,
                { signal },
              );
            } catch (e) {
              if (e.name === "AbortError") throw e;
              return { ...repo, tags: [] };
            }

            // Step C: fetch detail for every tag in parallel.
            const tagDetails = await Promise.all(
              tags.map(async (tag) => {
                const detailResp = await fetch(
                  `${gitlabApiPrefix}/api/v4/projects/${projectId}/registry/repositories/${repo.id}/tags/${encodeURIComponent(tag.name)}`,
                  { signal },
                );
                if (!detailResp.ok) {
                  return {
                    name: tag.name,
                    digest: null,
                    total_size: null,
                    created_at: null,
                  };
                }
                return detailResp.json();
              }),
            );

            return { ...repo, tags: tagDetails };
          }),
        );

        if (signal.aborted) return;
        setRepositories(reposWithTags);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("[useGitLabRegistryTags] Failed to fetch registry:", err);
        setError(err.message);
        setRepositories([]);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetchRegistry();

    return () => controller.abort();
  }, [projectId]);

  return { repositories, loading, error };
};
